/*
    Extension of Backbone Collection, used to represent a collection of v5 api 'device_group's
    Usage:
        var Backbone = require('backbone');
    var Radio = require('backbone.radio');
        var apiCh = Radio.channel('api');
        var devicegroups = apiCh.request({{devicegroup_type}});
        devicegroups.{{whatever Backbone.Collection methods you please}}
    Arguments:
        devicegroup_type: String. One of the TYPES object values, below. Any will work as of this comment,
            as the type passed in does not determine the types of devicegroups present in the
            collection returned.
*/

import _ from 'underscore';
import Api from '@/lib/api/api';
import v5_Collection from '@/collections/v5/collection';
import DeviceGroup from '@/models/v5/device_group';

function search(collection, term, options) {
    var deferred = $.Deferred();
    function done(results) {
        deferred.resolve(results);
    }
    function fail() {
        deferred.reject.apply(this, arguments);
    }
    this.fetch({
            url: Api.urls.device_group_search({ term: term}),
            data: options
        })
        .done(done)
        .fail(fail.bind(this))
    ;
    // Returning a Promise so that only this function can modify
    // the Deferred object
    return deferred.promise();
}

var DeviceGroupsCollection = new v5_Collection();
var TYPES = {
    development: 'development_devicegroup',
    production: 'production_devicegroup',
    dut: 'dut_devicegroup',
    factoryfixture: 'factoryfixture_devicegroup',
    pre_factoryfixture: 'pre_factoryfixture_devicegroup',
    pre_production: 'pre_production_devicegroup',
    pre_dut: 'pre_dut_devicegroup',
};
_.extend(DeviceGroupsCollection, {
    url: function() {
        return Api.urls.device_groups();
    },
    model: DeviceGroup,
    create_development_devicegroup: function(model, options) {
        return this.create(_.extend({
            type: TYPES.development
        }, model), options);
    },
    create_factoryfixture_devicegroup: function(model, options) {
        return this.create(_.extend({
            type: TYPES.factoryfixture
        }, model), options);
    },
    create_pre_factoryfixture_devicegroup: function(model, options) {
        return this.create(_.extend({
            type: TYPES.pre_factoryfixture
        }, model), options);
    },
    create_dut_devicegroup: function (model, options) {
        return this.create(_.extend({
            type: TYPES.dut
        }, model), options);
    },
    create_pre_dut_devicegroup: function (model, options) {
        return this.create(_.extend({
            type: TYPES.pre_dut
        }, model), options);
    },
    create_pre_production_devicegroup: function (model, options) {
        return this.create(_.extend({
            type: TYPES.pre_production
        }, model), options);
    },
    create_production_devicegroup: function(model, options) {
        return this.create(_.extend({
            type: TYPES.production
        }, model), options);
    },
    create: function(model, options) {
        options = options || {};
        var no_relationships_specified = !options.product;
        let has_profile = false;
        let profile = {};
        if (no_relationships_specified) {
            throw new Error('Must pass a \'product\' in the options to create a new DeviceGroup');
        }
        if (options.profile) {
            has_profile = true;
            if (options.profile.id && options.profile.id != '') {
                profile.id = options.profile.id;
                profile.type = options.profile.type;
            } else {
                profile = null;
            }
        }

        /*
            * Two ways to pass the product:
            *   (1) as a raw resource object:
            *       {
            *         id: <uuid>,
            *         type: 'product'
            *       }
            *       OR
            *   (2) as a Product object with a toJSON method
            */
        var success = options.success;
        options.success = function(model, resp, options) {
            if (options.product.counts && options.product.counts.get('devicegroups')) {
                var dg_counts = options.product.counts.get('devicegroups');
                dg_counts[_.invert(TYPES)[model.type]]++;
                options.product.counts.set(dg_counts);
            }
            if (success) success(model, resp, options);
        };

        var relationships = {
            product: _.pick(options.product, 'id', 'type')
        };
        if (has_profile) relationships.profile = profile;
        if (model.type === TYPES.factoryfixture || model.type === TYPES.pre_factoryfixture) {
            relationships.production_target = _.pick(options.production_target, 'id', 'type');
            relationships.dut_target = _.pick(options.dut_target, 'id', 'type');
        }
        var data = {
            type: model.type || TYPES.development,
            attributes: _.omit(model, 'type'),
            relationships: relationships
        };

        return v5_Collection.prototype.create.call(this, data, options);
    },
    /**
     * Lookup function lets you get a promise for looking up a
     * model. The arguments for done are the same as the
     * success: (model, response, options)
     *
     * Stolen from http://tech.adroll.com/blog/web/2013/11/12/lazyloading-backbone-collection-with-promises.html
     */
    lookup: function(id) {
        var deferred = $.Deferred();
        var model = this.get(id);
        if ( !model ) model = this.add({id: id});
        function done() {
            deferred.resolve(model);
        }
        function fail() {
            this.remove({id: id});
            deferred.reject.apply(this, arguments);
        }
        model.fetch({'include' : 'current_deployment,owner'})
            .done(done)
            .fail(fail.bind(this));
        // Returning a Promise so that only this function can modify
        // the Deferred object
        return deferred.promise();
    },
});
DeviceGroupsCollection.TYPES_MAP = TYPES;
DeviceGroupsCollection.types = _.values(TYPES);
DeviceGroupsCollection.production_types =
    ['production_devicegroup', 'factoryfixture_devicegroup', 'dut_devicegroup'];
DeviceGroupsCollection.development_types =
    _(DeviceGroupsCollection.types)
    .without(DeviceGroupsCollection.production_types);
DeviceGroupsCollection.search = _.partial(search, DeviceGroupsCollection);
export default DeviceGroupsCollection;

